import { useCallback, useState } from 'react';
import { FormProvider } from 'react-hook-form';

import { getInputValidation } from '../../../../data/GraphQL/api/graphql-errors';
import {
  Button,
  ButtonIcon,
  ButtonType,
} from '../../../../elements/button/button';
import { InputFieldController } from '../../../../elements/form/controllers/input-field';
import { FormRow } from '../../../../elements/form/form';
import { CustomFormattedMessage } from '../../../../elements/intl/custom-formatted-message';
import { Modal, ModalWidth } from '../../../../elements/modal/modal';
import { useRegistrationEmailForm } from '../../form/use-registration-email-form';

/** The server's reason for refusing an address, as shown on the field. */
const refusalMessageIds: { [validation: string]: string } = {
  AlreadyExists: 'registrations.email.in-use',
  InvalidEmailAddress: 'common.errors.invalid-mail-address',
};

type RegistrationEmailPromptProps = {
  name: string;
  email: string;
  onConfirm: (email: string) => Promise<void>;
  onClose?: () => void;
};

/**
 * Change the address of one person before accepting. The server checks the
 * same rules as on acceptance; when it refuses, the reason is shown on the
 * field so the practice can try another address without leaving the prompt.
 */
export function RegistrationEmailPrompt({
  name,
  email,
  onConfirm,
  onClose,
}: RegistrationEmailPromptProps) {
  const { form, formIsValid } = useRegistrationEmailForm(email);

  const [submitting, setSubmitting] = useState<boolean>(false);

  const onHandleConfirm = useCallback(() => {
    form.handleSubmit(async values => {
      setSubmitting(true);

      try {
        await onConfirm(values.email);
      } catch (error) {
        const validation = getInputValidation(error)?.validation ?? '';

        // InputField translates the message itself, so pass the id.
        form.setError('email', {
          message: refusalMessageIds[validation] ?? 'common.error',
        });
      } finally {
        setSubmitting(false);
      }
    })();
  }, [form, onConfirm]);

  return (
    <Modal
      testId="registration-email"
      width={ModalWidth.SMALL}
      title={<CustomFormattedMessage id="registrations.email.title" />}
      onClose={onClose}
      buttons={
        <>
          <Button
            testId="registration-email-cancel"
            type={ButtonType.SECONDARY}
            inverted
            onClick={onClose}
          >
            <CustomFormattedMessage id="common.cancel" />
          </Button>

          <Button
            testId="registration-email-confirm"
            icon={submitting ? ButtonIcon.LOADING : ButtonIcon.NONE}
            disabled={!formIsValid || submitting}
            onClick={onHandleConfirm}
          >
            <CustomFormattedMessage id="common.save" />
          </Button>
        </>
      }
    >
      <p>
        <CustomFormattedMessage
          id="registrations.email.content"
          values={{ name }}
        />
      </p>

      <FormProvider {...form}>
        <FormRow>
          <InputFieldController
            name="email"
            type="email"
            label={<CustomFormattedMessage id="common.email" />}
          />
        </FormRow>
      </FormProvider>
    </Modal>
  );
}
